import {
  DOCUMENT,
  DestroyRef,
  Injectable,
  Signal,
  WritableSignal,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { fromEvent } from 'rxjs';

import {
  SEATING_PLAN_DESIGNER_MIN_HEIGHT,
  SEATING_PLAN_DESIGNER_MIN_WIDTH,
  SeatingPlanDesignerBlockReason,
  SeatingPlanDesignerViewport,
} from '../models/seating-plan-designer-environment.model';


const COARSE_POINTER_QUERY = '(pointer: coarse)';
const FINE_POINTER_QUERY = '(pointer: fine)';

@Injectable({ providedIn: 'root' })
export class SeatingPlanDesignerEnvironmentService {
  public readonly viewport: Signal<SeatingPlanDesignerViewport>;
  public readonly isTouchOnly: Signal<boolean>;
  public readonly blockReason: Signal<SeatingPlanDesignerBlockReason | null>;
  public readonly isSupported: Signal<boolean>;
  public readonly minWidth = SEATING_PLAN_DESIGNER_MIN_WIDTH;
  public readonly minHeight = SEATING_PLAN_DESIGNER_MIN_HEIGHT;

  private readonly view = inject(DOCUMENT).defaultView;
  private readonly destroyRef = inject(DestroyRef);
  private readonly viewportSignal: WritableSignal<SeatingPlanDesignerViewport> = signal({
    width: 0,
    height: 0,
  });
  private readonly touchOnlySignal: WritableSignal<boolean> = signal(false);

  constructor() {
    this.viewport = this.viewportSignal.asReadonly();
    this.isTouchOnly = this.touchOnlySignal.asReadonly();
    this.blockReason = computed(() => {
      if (this.touchOnlySignal()) {
        return 'touch';
      }
      const { width, height } = this.viewportSignal();

      return width < SEATING_PLAN_DESIGNER_MIN_WIDTH || height < SEATING_PLAN_DESIGNER_MIN_HEIGHT
        ? 'size'
        : null;
    });
    this.isSupported = computed(() => this.blockReason() === null);
    this.refresh();
    if (this.view) {
      fromEvent(this.view, 'resize')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => this.refresh());
    }
  }

  public refresh(): void {
    const view = this.view;

    if (!view) {
      return;
    }
    this.viewportSignal.set({ width: view.innerWidth, height: view.innerHeight });
    this.touchOnlySignal.set(
      this.matches(COARSE_POINTER_QUERY) && !this.matches(FINE_POINTER_QUERY),
    );
  }

  private matches(query: string): boolean {
    const view = this.view;

    if (!view || typeof view.matchMedia !== 'function') {
      return false;
    }

    return view.matchMedia(query).matches;
  }
}
